// File: src/components/chat/ChatWindow/ChatWindow.tsx
// Purpose: Chat-related UI component

import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { MessageBubble } from "../MessageBubble/MessageBubble";
import { ChatInputBox } from "../ChatInputBox/ChatInputBox";
import { TypingIndicator } from "../TypingIndicator/TypingIndicator";
import { Typography } from "../../general/Typography"
// ✅ Wrapper for full-width responsiveness
const ChatWrapper = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: 100%; // ✅ Prevents excessive width
  height: 100%;
  margin: auto;
  background: ${({ theme }) => theme.colors.surface};
  border-radius: ${({ theme }) => theme.radii.md};
  box-shadow: ${({ theme }) => theme.elevations.md};
`;

// ✅ Chat Messages Container
const MessagesContainer = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: ${({ theme }) => theme.spacing.md};
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.sm};
`;

// ✅ Timestamp Styling
const Timestamp = styled(Typography)`
  font-size: ${({ theme }) => theme.typography.caption.fontSize};
  color: ${({ theme }) => theme.colors.textSecondary};
  text-align: center;
  margin-top: ${({ theme }) => theme.spacing.xxs};
`;

// ✅ Chat Message Type
export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp?: string;
}

// ✅ ChatWindow Props
export interface ChatWindowProps {
  messages: ChatMessage[];
  onSend: (message: string) => void;
  isTyping?: boolean;
}

// 🚀 **ChatWindow Component**
export const ChatWindow: React.FC<ChatWindowProps> = ({ messages, onSend, isTyping = false }) => {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // ✅ Scroll to Bottom when New Message Appears
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const handleSend = () => {
    if (!input.trim()) return;
    onSend(input.trim());
    setInput("");
  };

  return (
    <ChatWrapper>
      <MessagesContainer>
        {messages?.map((msg) => (
          <div key={msg.id}>
            <MessageBubble isUser={msg.isUser}>{msg.text}</MessageBubble>
            <Timestamp>{msg.timestamp}</Timestamp>
          </div>
        ))}
        {isTyping && <TypingIndicator />}
        <div ref={messagesEndRef} /> {/* ✅ Auto-scroll target */}
      </MessagesContainer>

      {/* ✅ Chat Input Box */}
      <ChatInputBox value={input} onChange={setInput} onSend={handleSend} />
    </ChatWrapper>
  );
};